import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plane, Search } from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';
import {
    Avatar, Cargando, DIAS_LETRA, Flotante, MESES, NombrePersona, aFecha, botonClaro, campo, capital, diaSemana, display,
    fechaCorta, horaAmPm, iso, panel,
} from '../ui';

const ANCHO_DIA = 32;

const COLORES = {
    vacaciones: { lleno: 'bg-emerald-500 text-white', pendiente: 'border-2 border-dashed border-emerald-500 text-emerald-700 bg-emerald-50' },
    permiso: { lleno: 'bg-cyan-500 text-white', pendiente: 'border-2 border-dashed border-cyan-500 text-cyan-700 bg-cyan-50' },
};

const detalle = (i) => {
    if (i.hora_inicio) return `${i.titulo}, ${fechaCorta(i.desde)} de ${horaAmPm(i.hora_inicio)} a ${horaAmPm(i.hora_fin)}`;
    const dias = Math.round((aFecha(i.hasta) - aFecha(i.desde)) / 86400000) + 1;
    const rango = i.desde === i.hasta ? fechaCorta(i.desde) : `del ${fechaCorta(i.desde)} al ${fechaCorta(i.hasta)}`;
    return `${i.titulo} ${rango} (${dias} ${dias === 1 ? 'día' : 'días'})${i.reintegro ? `, vuelve el ${fechaCorta(i.reintegro)}` : ''}`;
};

// Línea de tiempo del mes: una fila por persona, una barra por vacación o permiso
const AusenciasVista = () => {
    const ahora = new Date();
    const [mes, setMes] = useState(new Date(ahora.getFullYear(), ahora.getMonth(), 1));
    const [texto, setTexto] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [sedeId, setSedeId] = useState('');
    const [filtros, setFiltros] = useState({ sedes: [] });
    const [items, setItems] = useState(null);
    const [feriados, setFeriados] = useState({});
    const [encima, setEncima] = useState(null);

    const primero = iso(mes);
    const ultimoDia = useMemo(() => new Date(mes.getFullYear(), mes.getMonth() + 1, 0), [mes]);
    const ultimo = iso(ultimoDia);
    const dias = useMemo(() => Array.from({ length: ultimoDia.getDate() }, (_, i) => new Date(mes.getFullYear(), mes.getMonth(), i + 1)), [mes, ultimoDia]);

    useEffect(() => {
        axiosInstance.get('/api/asistencia/filtros').then(({ data }) => setFiltros(data)).catch(() => {});
    }, []);
    useEffect(() => {
        const t = setTimeout(() => setBusqueda(texto.trim()), 300);
        return () => clearTimeout(t);
    }, [texto]);
    useEffect(() => {
        axiosInstance.get('/api/asistencia/dias-no-laborables', { params: { anio: mes.getFullYear() } })
            .then(({ data }) => setFeriados(Object.fromEntries(data.dias.map((d) => [d.fecha, d.descripcion])))).catch(() => {});
    }, [mes]);
    useEffect(() => {
        let vivo = true;
        setItems(null);
        axiosInstance.get('/api/asistencia/vacaciones-permisos', {
            params: { desde: primero, hasta: ultimo, sede_id: sedeId || undefined, q: busqueda || undefined },
        }).then(({ data }) => vivo && setItems(data.items)).catch(() => vivo && setItems([]));
        return () => { vivo = false; };
    }, [primero, ultimo, sedeId, busqueda]);

    const filas = useMemo(() => {
        const porPersona = new Map();
        (items || []).forEach((i) => {
            if (!porPersona.has(i.empleado_id)) porPersona.set(i.empleado_id, { persona: i, items: [] });
            porPersona.get(i.empleado_id).items.push(i);
        });
        return [...porPersona.values()].sort((a, b) => (a.persona.nombre || '').localeCompare(b.persona.nombre || ''));
    }, [items]);

    // Cuántos están fuera cada día (para la franja de arriba)
    const fueraPorDia = useMemo(() => dias.map((d) => {
        const k = iso(d);
        return (items || []).filter((i) => !i.hora_inicio && i.desde <= k && i.hasta >= k).length;
    }), [dias, items]);
    const maxFuera = Math.max(1, ...fueraPorDia);

    const cambiarMes = (n) => setMes((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));
    const hoy = iso(ahora);
    const vacaciones = (items || []).filter((i) => i.tipo === 'vacaciones').length;
    const permisos = (items || []).length - vacaciones;
    const plantilla = `15rem repeat(${dias.length}, ${ANCHO_DIA}px)`;
    const posicion = (i) => {
        const ini = Math.max(0, aFecha(i.desde) < mes ? 0 : aFecha(i.desde).getDate() - 1);
        const fin = aFecha(i.hasta) > ultimoDia ? dias.length - 1 : aFecha(i.hasta).getDate() - 1;
        return { ini, fin, cortaInicio: aFecha(i.desde) < mes, cortaFin: aFecha(i.hasta) > ultimoDia };
    };

    return (
        <div className="space-y-5">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className={`${display} text-3xl font-bold text-slate-900`}>Vacaciones y permisos</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {items === null ? 'Buscando...' : items.length
                            ? `${vacaciones} ${vacaciones === 1 ? 'vacación' : 'vacaciones'} y ${permisos} ${permisos === 1 ? 'permiso' : 'permisos'} en ${MESES[mes.getMonth()]}.`
                            : `Nadie tiene vacaciones ni permisos en ${MESES[mes.getMonth()]}.`}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => cambiarMes(-1)} className={botonClaro} aria-label="Mes anterior"><ChevronLeft size={16} /></button>
                    <span className={`${display} w-40 text-center text-lg font-semibold text-slate-800`}>{capital(MESES[mes.getMonth()])} {mes.getFullYear()}</span>
                    <button type="button" onClick={() => cambiarMes(1)} className={botonClaro} aria-label="Mes siguiente"><ChevronRight size={16} /></button>
                    <button type="button" onClick={() => setMes(new Date(ahora.getFullYear(), ahora.getMonth(), 1))} className={botonClaro}>Este mes</button>
                </div>
            </header>

            <div className="flex flex-wrap items-center gap-3">
                <select value={sedeId} onChange={(e) => setSedeId(e.target.value)} className={campo} aria-label="Sede">
                    <option value="">Todas las sedes</option>
                    {filtros.sedes.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                </select>
                <div className="relative flex-1 min-w-[12rem] max-w-sm">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar persona o cédula" className={`${campo} w-full pl-9`} />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 ml-auto">
                    <span className="inline-flex items-center gap-1.5"><span className="w-5 h-2.5 rounded-full bg-emerald-500" />Vacaciones</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-5 h-2.5 rounded-full bg-cyan-500" />Permiso</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-5 h-2.5 rounded-full border-2 border-dashed border-slate-400" />Por aprobar</span>
                </div>
            </div>

            {items === null ? <Cargando alto="h-80" /> : (
                <div className={`${panel} overflow-x-auto scroll-fino`}>
                    <div className="grid w-max min-w-full" style={{ gridTemplateColumns: plantilla }}>
                        {/* Cuántos están fuera cada día */}
                        <div className="sticky left-0 z-10 bg-white px-4 pt-4 pb-2 flex items-end">
                            <span className="text-xs text-slate-400">Fuera por día</span>
                        </div>
                        {fueraPorDia.map((n, i) => (
                            <div key={i} className="pt-4 pb-2 flex items-end justify-center h-16" title={`${n} fuera`}>
                                {n > 0 && (
                                    <div className="w-4 rounded-t bg-emerald-200 relative" style={{ height: `${(n / maxFuera) * 36 + 4}px` }}>
                                        <span className="absolute -top-4 inset-x-0 text-center text-[10px] tabular-nums text-slate-500">{n}</span>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Días del mes */}
                        <div className="sticky left-0 z-10 bg-white border-y border-slate-200" />
                        {dias.map((d) => {
                            const k = iso(d);
                            const libre = diaSemana(d) >= 5 || feriados[k];
                            return (
                                <div key={k} title={feriados[k] || undefined}
                                    className={`border-y border-slate-200 py-1.5 flex flex-col items-center ${k === hoy ? 'bg-cyan-50' : libre ? 'bg-slate-50' : ''}`}>
                                    <span className={`text-[10px] ${feriados[k] ? 'text-rose-400' : 'text-slate-400'}`}>{DIAS_LETRA[diaSemana(d)]}</span>
                                    <span className={`text-xs tabular-nums font-medium ${k === hoy ? 'text-cyan-700' : libre ? 'text-slate-400' : 'text-slate-700'}`}>{d.getDate()}</span>
                                </div>
                            );
                        })}

                        {filas.length === 0 && (
                            <div className="col-span-full py-16 flex flex-col items-center text-center">
                                <span className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                                    <Plane size={20} className="text-emerald-600" />
                                </span>
                                <p className="text-sm font-medium text-slate-700">Nadie fuera en {MESES[mes.getMonth()]}</p>
                                <p className="text-sm text-slate-500 mt-1 max-w-sm">
                                    Las vacaciones y permisos que se piden en Centro SURA aparecen aquí, aprobados o por aprobar.
                                </p>
                            </div>
                        )}

                        {filas.map(({ persona, items: suyos }) => (
                            <React.Fragment key={persona.empleado_id}>
                                <div className="sticky left-0 z-10 bg-white flex items-center gap-2.5 px-4 py-2 border-b border-slate-100">
                                    <Avatar persona={persona} size={28} />
                                    <div className="min-w-0">
                                        <NombrePersona persona={persona} className="block text-sm font-medium text-slate-900 truncate max-w-[10.5rem]" />
                                        <p className="text-[11px] text-slate-400 truncate max-w-[10.5rem]">{persona.departamento}</p>
                                    </div>
                                </div>
                                <div className="relative border-b border-slate-100" style={{ gridColumn: `span ${dias.length}` }}>
                                    {/* Fondo: fines de semana, feriados y hoy */}
                                    <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${dias.length}, ${ANCHO_DIA}px)` }} aria-hidden>
                                        {dias.map((d) => {
                                            const k = iso(d);
                                            return <div key={k} className={k === hoy ? 'bg-cyan-50/70' : diaSemana(d) >= 5 || feriados[k] ? 'bg-slate-50' : ''} />;
                                        })}
                                    </div>
                                    {suyos.map((i) => {
                                        const { ini, fin, cortaInicio, cortaFin } = posicion(i);
                                        const colores = COLORES[i.tipo] || COLORES.permiso;
                                        const ancho = (fin - ini + 1) * ANCHO_DIA - 6;
                                        const porHoras = !!i.hora_inicio;
                                        return (
                                            <button
                                                key={i.codigo} type="button"
                                                onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setEncima({ i, x: r.left + r.width / 2, y: r.top }); }}
                                                onMouseLeave={() => setEncima(null)}
                                                onFocus={(e) => { const r = e.currentTarget.getBoundingClientRect(); setEncima({ i, x: r.left + r.width / 2, y: r.top }); }}
                                                onBlur={() => setEncima(null)}
                                                aria-label={`${persona.nombre}: ${detalle(i)}, ${i.estado}`}
                                                className={`absolute top-1/2 -translate-y-1/2 h-7 text-[11px] font-medium px-2 truncate text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
                                                    i.aprobada ? colores.lleno : colores.pendiente
                                                } ${cortaInicio ? 'rounded-l-none' : 'rounded-l-full'} ${cortaFin ? 'rounded-r-none' : 'rounded-r-full'}`}
                                                style={{ left: ini * ANCHO_DIA + 3, width: porHoras ? ANCHO_DIA - 6 : ancho }}
                                            >
                                                {!porHoras && ancho > 90 && i.titulo}
                                            </button>
                                        );
                                    })}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            {encima && (
                <Flotante>
                <div className="fixed z-50 pointer-events-none -translate-x-1/2 -translate-y-full px-3 py-2 rounded-xl bg-slate-900 text-white shadow-lg max-w-xs"
                    style={{ left: encima.x, top: encima.y - 8 }}>
                    <p className="text-sm font-medium">{encima.i.nombre}</p>
                    <p className="text-xs text-slate-300">{detalle(encima.i)}</p>
                    <p className={`text-xs ${encima.i.aprobada ? 'text-emerald-300' : 'text-amber-300'}`}>{encima.i.estado}</p>
                </div>
                </Flotante>
            )}
        </div>
    );
};

export default AusenciasVista;
